import path from 'path'
import { isExternal } from '@/utils/validate'

const MAX_RESULTS = 20

function resolvePath(basePath, routePath) {
  if (isExternal(routePath)) return routePath
  if (isExternal(basePath)) return basePath
  return path.resolve(basePath, routePath || '')
}

function displayName(route) {
  return String((route.meta && route.meta.title) || route.name || '').trim()
}

export function buildMenuIndex(routes) {
  const entries = []

  function add(url, ancestors, title) {
    if (!title || !url) return
    const parents = ancestors.filter(Boolean)
    entries.push({
      path: url,
      title,
      parents,
      titleLower: title.toLocaleLowerCase(),
      parentLower: parents.join(' ').toLocaleLowerCase(),
      pinyin: '',
      initials: '',
      parentPinyin: '',
      parentInitials: '',
      order: entries.length
    })
  }

  function visit(route, basePath, ancestors) {
    if (!route || route.hidden || route.path === '*') return
    const url = resolvePath(basePath, route.path)
    const title = displayName(route)
    const showingChildren = (route.children || []).filter(child => !child.hidden)
    const isSingle = !route.alwaysShow && showingChildren.length <= 1

    if (isSingle && showingChildren.length === 0) {
      // SidebarItem 将没有可见子项的目录显示为可点击菜单。
      if (route.meta) add(url, ancestors, route.name || title)
      return
    }
    if (isSingle && showingChildren.length === 1) {
      const child = showingChildren[0]
      if (!(child.children || []).some(grandchild => !grandchild.hidden) && child.meta) {
        add(resolvePath(url, child.path), ancestors.concat(title), child.name || displayName(child))
        return
      }
    }
    showingChildren.forEach(child => visit(child, url, ancestors.concat(title)))
  }

  const menuRoutes = routes || []
  menuRoutes.forEach(route => visit(route, '/', []))
  return entries
}

export function addPinyin(index, pinyin) {
  index.forEach(entry => {
    const syllables = pinyin(entry.title, { toneType: 'none', type: 'array' })
    entry.pinyin = syllables.join('').toLocaleLowerCase()
    entry.initials = syllables.map(syllable => syllable.charAt(0)).join('').toLocaleLowerCase()
    const parentSyllables = pinyin(entry.parents.join(''), { toneType: 'none', type: 'array' })
    entry.parentPinyin = parentSyllables.join('').toLocaleLowerCase()
    entry.parentInitials = parentSyllables.map(syllable => syllable.charAt(0)).join('').toLocaleLowerCase()
  })
}

function rank(entry, terms, query) {
  const matched = terms.every(term =>
    entry.titleLower.includes(term) ||
    entry.pinyin.includes(term) ||
    entry.initials.includes(term) ||
    entry.parentLower.includes(term) ||
    entry.parentPinyin.includes(term) ||
    entry.parentInitials.includes(term)
  )
  if (!matched) return -1
  if (entry.titleLower === query) return 0
  if (entry.titleLower.startsWith(query)) return 1
  if (entry.titleLower.includes(query)) return 2
  if (entry.pinyin.includes(query)) return 3
  if (entry.initials.includes(query)) return 4
  return 5
}

export function searchMenuIndex(index, input) {
  const query = String(input || '').trim().toLocaleLowerCase().replace(/\s+/g, ' ')
  if (!query) return { results: [], overflow: false }
  const terms = query.split(' ')
  const matches = []
  index.forEach(entry => {
    const score = rank(entry, terms, query)
    if (score !== -1) matches.push({ entry, score })
  })
  matches.sort((a, b) => a.score - b.score || a.entry.order - b.entry.order)
  return {
    results: matches.slice(0, MAX_RESULTS).map(item => item.entry),
    overflow: matches.length > MAX_RESULTS
  }
}
